import { type ReactNode, createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from './icons';

export interface ToastOptions {
  type?: 'success' | 'error' | 'info';
  action?: { label: string; onClick: () => void };
  duration?: number;
}
interface Toast extends ToastOptions {
  id: number;
  message: ReactNode;
  shown: boolean;
}
type ToastFn = (message: ReactNode, o?: ToastOptions) => () => void;

const Ctx = createContext<ToastFn | null>(null);
export function useToast(): ToastFn {
  const c = useContext(Ctx);
  if (!c) throw new Error('useToast() must be used inside <ToastProvider>');
  return c;
}

const ICON: Record<NonNullable<ToastOptions['type']>, string> = { success: '✓', error: '!', info: 'i' };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => {
    setItems((t) => t.map((x) => (x.id === id ? { ...x, shown: false } : x)));
    window.setTimeout(() => setItems((t) => t.filter((x) => x.id !== id)), 200);
  }, []);
  const toast = useCallback<ToastFn>(
    (message, o = {}) => {
      const id = ++seq.current;
      setItems((t) => [...t, { id, message, shown: false, ...o }]);
      requestAnimationFrame(() => setItems((t) => t.map((x) => (x.id === id ? { ...x, shown: true } : x))));
      const timer = window.setTimeout(() => dismiss(id), o.duration ?? (o.action ? 6000 : 3500));
      return () => {
        window.clearTimeout(timer);
        dismiss(id);
      };
    },
    [dismiss],
  );
  const value = useMemo(() => toast, [toast]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {createPortal(
        <div className="ui-toasts" role="status" aria-live="polite">
          {items.map((t) => (
            <div key={t.id} className={`ui-toast ${t.type ?? 'success'}${t.shown ? ' in' : ''}`}>
              <span className="ico" aria-hidden="true">{ICON[t.type ?? 'success']}</span>
              <span className="msg">{t.message}</span>
              {t.action && (
                <button type="button" onClick={() => { t.action?.onClick(); dismiss(t.id); }}>{t.action.label}</button>
              )}
              <button type="button" className="x" aria-label="Dismiss" onClick={() => dismiss(t.id)}><X /></button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}
